import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import parseLinkHeader from "parse-link-header";
import {
  loadProjectPending,
  loadProjectFulfilled,
  loadProjectRejected,
} from "./reducers/loadProjectReducers";
import {
  createOrUpdateProject,
  readProject,
  loadRemix,
  createRemix,
  deleteProject,
  readProjectList,
  getProjectContext,
  createProjectFeedback,
  readProjectFeedbackList,
  markProjectFeedbackAsRead,
  getProjectStatus,
  setProjectStatusUnsubmitted,
  setProjectStatusSubmitted,
  setProjectStatusComplete,
  getRemixIdentifier,
  setProjectStatusReturned,
  deleteProjectFeedback,
} from "../utils/apiCallHandler";
import { asyncThunkRequestStatuses } from "../utils/stateEnums";

export const syncProject = (actionName, isLoading = false) =>
  createAsyncThunk(
    `editor/${actionName}Project`,
    async (
      { project, identifier, locale, accessToken, autosave },
      { rejectWithValue },
    ) => {
      let response;
      switch (actionName) {
        case "load":
          response = await readProject(identifier, locale, accessToken);
          break;
        case "loadRemix":
          response = await loadRemix(identifier, accessToken);
          break;
        case "remix":
          response = await createRemix(project, accessToken);
          break;
        case "save":
          response = await createOrUpdateProject(project, accessToken);
          break;
        case "delete":
          response = await deleteProject(identifier, accessToken);
          break;
        default:
          rejectWithValue({ error: "no such sync action" });
      }
      return { project: response.data, autosave };
    },
    {
      condition: (_, { getState }) => {
        const { editor } = getState();
        const saveStatus = editor.saving;
        const loadStatus = editor.loading;
        if (isLoading) {
          return false;
        }
        if (
          (actionName === "save" || actionName === "remix") &&
          saveStatus === "pending"
        ) {
          return false;
        }
        if (actionName === "load" && loadStatus === "pending") {
          return false;
        }
      },
    },
  );

export const loadProjectList = createAsyncThunk(
  `editor/loadProjectList`,
  async ({ page, accessToken }) => {
    const response = await readProjectList(page, accessToken);
    return {
      projects: response.data,
      page,
      links: parseLinkHeader(response.headers.link),
    };
  },
);

export const loadProjectContext = createAsyncThunk(
  `editor/loadProjectContext`,
  async ({ identifier, accessToken }) => {
    const response = await getProjectContext(identifier, accessToken);
    return response.data;
  },
);

export const loadRemixIdentifier = createAsyncThunk(
  `editor/loadRemixIdentifier`,
  async ({ projectIdentifier, accessToken }, { rejectWithValue }) => {
    try {
      const response = await getRemixIdentifier(projectIdentifier, accessToken);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error?.response?.status,
        message: error?.response?.data?.message || error?.message,
      });
    }
  },
);

export const createNewProjectFeedback = createAsyncThunk(
  `editor/createProjectFeedback`,
  async ({ projectIdentifier, accessToken, content }) => {
    const response = await createProjectFeedback(
      projectIdentifier,
      accessToken,
      content,
    );
    return response.data;
  },
);

export const loadProjectFeedbackList = createAsyncThunk(
  `editor/loadProjectFeedbackList`,
  async ({ projectIdentifier, accessToken }) => {
    const response = await readProjectFeedbackList(
      projectIdentifier,
      accessToken,
    );
    return response.data;
  },
);

export const setProjectFeedbackItemRead = createAsyncThunk(
  `editor/setProjectFeedbackItemRead`,
  async ({ projectIdentifier, accessToken, feedbackId }) => {
    const response = await markProjectFeedbackAsRead(
      projectIdentifier,
      accessToken,
      feedbackId,
    );
    return response.data;
  },
);

export const deleteProjectFeedbackItem = createAsyncThunk(
  `editor/deleteProjectFeedback`,
  async ({ projectIdentifier, feedbackId, accessToken }) => {
    const response = await deleteProjectFeedback(
      projectIdentifier,
      accessToken,
      feedbackId,
    );
    return response.data;
  },
);

export const loadProjectStatus = createAsyncThunk(
  `editor/loadProjectStatus`,
  async ({ projectIdentifier, accessToken }) => {
    const response = await getProjectStatus(projectIdentifier, accessToken);
    return response.data;
  },
);

export const submitProject = createAsyncThunk(
  `editor/submitProject`,
  async ({ projectIdentifier, accessToken }) => {
    const response = await setProjectStatusSubmitted(
      projectIdentifier,
      accessToken,
    );
    return response.data;
  },
);

export const unsubmitProject = createAsyncThunk(
  `editor/unsubmitProject`,
  async ({ projectIdentifier, accessToken }) => {
    const response = await setProjectStatusUnsubmitted(
      projectIdentifier,
      accessToken,
    );
    return response.data;
  },
);

export const returnProject = createAsyncThunk(
  `editor/returnProject`,
  async ({ projectIdentifier, accessToken }) => {
    const response = await setProjectStatusReturned(
      projectIdentifier,
      accessToken,
    );
    return response.data;
  },
);

export const completeProject = createAsyncThunk(
  `editor/completeProject`,
  async ({ projectIdentifier, accessToken }) => {
    const response = await setProjectStatusComplete(
      projectIdentifier,
      accessToken,
    );
    return response.data;
  },
);

export const EditorSlice = createSlice({
  name: "editor",
  initialState: {
    userType: "full",
    project: {},
    saveTriggered: false,
    saving: "idle",
    loading: "idle",
    justLoaded: false,
    remixLoadFailed: false,
    feedbackLoading: asyncThunkRequestStatuses.idle,
    loadError: "",
    saveError: "",
    currentLoadingRequestId: undefined,
    failedLoadIdentifier: undefined,
    openFiles: [[]],
    focussedFileIndices: [0],
    nameError: "",
    autorunEnabled: false,
    codeRunTriggered: false,
    codeHasBeenRun: false,
    drawTriggered: false,
    isEmbedded: false,
    browserPreview: false,
    isSplitView: true,
    isThemeable: true,
    webComponent: false,
    codeRunLoading: false,
    codeRunStopped: false,
    projectList: [],
    projectListLoaded: "idle",
    projectIndexCurrentPage: 1,
    projectIndexTotalPages: 1,
    lastSaveAutosave: false,
    lastSavedTime: null,
    senseHatAlwaysEnabled: false,
    senseHatEnabled: false,
    accessDeniedNoAuthModalShowing: false,
    accessDeniedWithAuthModalShowing: false,
    notFoundModalShowing: false,
    projectModalShowing: false,
    renameProjectModalShowing: false,
    deleteProjectModalShowing: false,
    modals: {},
  },
  reducers: {
    setIsRemix: (state, action) => {
      state.project.isRemix = action.payload;
    },
    updateUserType: (state, action) => {
      state.userType = action.payload;
    },
    closeFile: (state, action) => {
      const panelIndex = state.openFiles
        .map((fileNames) => fileNames.includes(action.payload))
        .indexOf(true);
      const closedFileIndex = state.openFiles[panelIndex].indexOf(
        action.payload,
      );
      state.openFiles[panelIndex] = state.openFiles[panelIndex].filter(
        (fileName) => fileName !== action.payload,
      );
      if (
        state.focussedFileIndices[panelIndex] >=
          state.openFiles[panelIndex].length ||
        closedFileIndex < state.focussedFileIndices[panelIndex]
      ) {
        state.focussedFileIndices[panelIndex]--;
      }
    },
    openFile: (state, action) => {
      const firstPanelIndex = 0;
      if (!state.openFiles.flat().includes(action.payload)) {
        state.openFiles[firstPanelIndex].push(action.payload);
      }
      state.focussedFileIndices[firstPanelIndex] = state.openFiles[
        firstPanelIndex
      ].indexOf(action.payload);
    },
    setOpenFiles: (state, action) => {
      state.openFiles = action.payload;
    },
    addFilePanel: (state) => {
      state.openFiles.push([]);
      state.focussedFileIndices.push(0);
    },
    setFocussedFileIndex: (state, action) => {
      state.focussedFileIndices[action.payload.panelIndex] =
        action.payload.fileIndex;
    },
    updateImages: (state, action) => {
      if (!state.project.image_list) {
        state.project.image_list = [];
      }
      state.project.image_list = action.payload;
    },
    setWebComponent: (state, action) => {
      state.webComponent = action.payload;
    },
    addProjectComponent: (state, action) => {
      state.project.components.push({
        name: action.payload.name,
        extension: action.payload.extension,
        content: "",
      });
      state.saving = "idle";
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setEmbedded: (state, _action) => {
      state.isEmbedded = true;
    },
    setBrowserPreview: (state, _action) => {
      state.browserPreview = true;
    },
    setIsSplitView: (state, action) => {
      state.isSplitView = action.payload;
    },
    setNameError: (state, action) => {
      state.nameError = action.payload;
    },
    setProject: (state, action) => {
      state.project = action.payload;
      if (!state.project.image_list) {
        state.project.image_list = [];
      }
      state.loading = "success";
      if (state.openFiles.flat().length === 0) {
        const firstPanelIndex = 0;
        if (state.project.project_type === "html") {
          state.openFiles[firstPanelIndex].push("index.html");
        } else {
          state.openFiles[firstPanelIndex].push("main.py");
        }
      }
      state.justLoaded = true;
    },
    setProjectIdentifier: (state, action) => {
      state.project.identifier = action.payload;
    },
    expireJustLoaded: (state) => {
      state.justLoaded = false;
    },
    setSenseHatAlwaysEnabled: (state, action) => {
      state.senseHatAlwaysEnabled = action.payload;
    },
    setSenseHatEnabled: (state, action) => {
      state.senseHatEnabled = action.payload;
    },
    triggerDraw: (state) => {
      state.drawTriggered = true;
    },
    triggerSave: (state) => {
      state.saveTriggered = true;
    },
    updateProjectComponent: (state, action) => {
      const extension = action.payload.extension;
      const fileName = action.payload.name;
      const code = action.payload.code;

      const mapped = state.project.components.map((item) => {
        if (item.extension !== extension || item.name !== fileName) {
          return item;
        }

        return { ...item, ...{ content: code } };
      });
      state.project.components = mapped;
    },
    updateProjectName: (state, action) => {
      state.project.name = action.payload;
      state.saving = "idle";
    },
    updateComponentName: (state, action) => {
      const key = action.payload.key;
      const name = action.payload.name;
      const extension = action.payload.extension;
      const oldName = `${state.project.components[key].name}.${state.project.components[key].extension}`;
      state.project.components[key].name = name;
      state.project.components[key].extension = extension;
      if (state.openFiles.flat().includes(oldName)) {
        const panelIndex = state.openFiles
          .map((fileNames) => fileNames.includes(oldName))
          .indexOf(true);
        const fileIndex = state.openFiles[panelIndex].indexOf(oldName);
        state.openFiles[panelIndex][fileIndex] = `${name}.${extension}`;
      }
      state.saving = "idle";
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    triggerCodeRun: (state) => {
      state.codeRunTriggered = true;
      state.codeHasBeenRun = true;
    },
    stopCodeRun: (state) => {
      state.codeRunStopped = true;
    },
    stopDraw: (state) => {
      state.drawTriggered = false;
    },
    loadingRunner: (state) => {
      state.codeRunLoading = true;
    },
    codeRunHandled: (state) => {
      state.codeRunLoading = false;
      state.codeRunTriggered = false;
      state.codeRunStopped = false;
    },
    closeAccessDeniedNoAuthModal: (state) => {
      state.accessDeniedNoAuthModalShowing = false;
      state.modals = {};
    },
    closeAccessDeniedWithAuthModal: (state) => {
      state.accessDeniedWithAuthModalShowing = false;
    },
    closeNotFoundModal: (state) => {
      state.notFoundModalShowing = false;
    },
    resetProjectLoadState: (state) => {
      state.loading = "idle";
      state.failedLoadIdentifier = undefined;
      state.notFoundModalShowing = false;
      state.accessDeniedWithAuthModalShowing = false;
      state.accessDeniedNoAuthModalShowing = false;
      state.modals = {};
      state.currentLoadingRequestId = undefined;
    },
    showProjectModal: (state) => {
      state.projectModalShowing = true;
    },
    closeProjectModal: (state) => {
      state.projectModalShowing = false;
    },
    showRenameProjectModal: (state, action) => {
      state.modals.renameProject = action.payload;
      state.renameProjectModalShowing = true;
    },
    closeRenameProjectModal: (state) => {
      state.modals.renameProject = null;
      state.renameProjectModalShowing = false;
    },
    showDeleteProjectModal: (state, action) => {
      state.modals.deleteProject = action.payload;
      state.deleteProjectModalShowing = true;
    },
    closeDeleteProjectModal: (state) => {
      state.modals.deleteProject = null;
      state.deleteProjectModalShowing = false;
    },
    setProjectIndexPage: (state, action) => {
      state.projectIndexCurrentPage = action.payload;
      state.projectListLoaded = "idle";
    },
    disableTheming: (state) => {
      state.isThemeable = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase("editor/saveProject/pending", (state) => {
      state.saving = "pending";
      state.saveTriggered = false;
    });
    builder.addCase("editor/saveProject/fulfilled", (state, action) => {
      localStorage.removeItem(state.project.identifier || "project");
      state.lastSaveAutosave = action.payload.autosave;
      state.saving = "success";
      state.lastSavedTime = Date.now();

      if (state.renameProjectModalShowing) {
        state.modals.renameProject = null;
        state.renameProjectModalShowing = false;
        state.projectListLoaded = "idle";
      } else if (
        state.project.identifier !== action.payload.project.identifier
      ) {
        state.project.image_list = state.project.image_list || [];
        state.project = action.payload.project;
        state.loading = "idle";
      }
    });
    builder.addCase("editor/saveProject/rejected", (state) => {
      state.saving = "failed";
    });
    builder.addCase("editor/remixProject/pending", (state, action) => {
      state.saving = "pending";
    });
    builder.addCase("editor/remixProject/fulfilled", (state, action) => {
      state.lastSaveAutosave = false;
      state.saving = "success";
      state.project = action.payload.project;
      state.loading = "idle";
    });
    builder.addCase("editor/loadRemixProject/pending", loadProjectPending);
    builder.addCase("editor/loadRemixProject/fulfilled", (state, action) => {
      loadProjectFulfilled(state, action);
      state.remixLoadFailed = false;
    });
    builder.addCase("editor/loadRemixProject/rejected", (state, action) => {
      loadProjectRejected(state, action);
      state.remixLoadFailed = true;
    });
    builder.addCase("editor/loadProject/pending", loadProjectPending);
    builder.addCase("editor/loadProject/fulfilled", loadProjectFulfilled);
    builder.addCase("editor/loadProject/rejected", loadProjectRejected);
    builder.addCase("editor/deleteProject/fulfilled", (state) => {
      state.projectListLoaded = "idle";
      state.modals.deleteProject = null;
      state.deleteProjectModalShowing = false;
    });
    builder.addCase("editor/loadProjectList/pending", (state) => {
      state.projectListLoaded = "pending";
    });
    builder.addCase("editor/loadProjectList/fulfilled", (state, action) => {
      if (action.payload.projects.length > 0 || action.payload.page === 1) {
        state.projectListLoaded = "success";
        state.projectList = action.payload.projects;
        const links = action.payload.links;
        state.projectIndexTotalPages =
          links && links.last ? parseInt(links.last.page) : action.payload.page;
      } else {
        state.projectIndexCurrentPage = state.projectIndexCurrentPage - 1;
        state.projectListLoaded = "idle";
      }
    });
    builder.addCase("editor/loadProjectList/rejected", (state) => {
      state.projectListLoaded = "failed";
    });
    builder.addCase("editor/loadProjectContext/pending", (state) => {
      state.loading = "pending";
    });
    builder.addCase("editor/loadProjectContext/rejected", (state) => {
      state.loading = "failed";
    });
    builder.addCase("editor/loadProjectContext/fulfilled", (state, action) => {
      state.loading = "success";
      state.project.context = action.payload;
    });
    builder.addCase("editor/loadRemixIdentifier/rejected", (state, action) => {
      state.project.identifier = action.meta.arg.projectIdentifier;
    });
    builder.addCase("editor/loadRemixIdentifier/fulfilled", (state, action) => {
      state.project.identifier = action.payload.identifier;
    });
    builder.addCase(
      "editor/createProjectFeedback/fulfilled",
      (state, action) => {
        state.project.feedback = [
          ...(state.project.feedback || []),
          action.payload,
        ];
      },
    );
    builder.addCase("editor/loadProjectFeedbackList/pending", (state) => {
      state.feedbackLoading = asyncThunkRequestStatuses.pending;
    });
    builder.addCase("editor/loadProjectFeedbackList/rejected", (state) => {
      state.feedbackLoading = asyncThunkRequestStatuses.rejected;
    });
    builder.addCase(
      "editor/loadProjectFeedbackList/fulfilled",
      (state, action) => {
        state.project.feedback = action.payload;
        state.feedbackLoading = asyncThunkRequestStatuses.fulfilled;
      },
    );
    builder.addCase(
      "editor/setProjectFeedbackItemRead/fulfilled",
      (state, action) => {
        const idx = state.project.feedback.findIndex(
          (item) => item.id === action.payload.id,
        );
        if (idx !== -1) {
          state.project.feedback[idx] = action.payload;
        }
      },
    );
    builder.addCase(
      "editor/deleteProjectFeedback/fulfilled",
      (state, action) => {
        state.project.feedback = state.project.feedback.filter(
          (item) => item.id !== action.meta.arg.feedbackId,
        );
      },
    );
    builder.addCase("editor/loadProjectStatus/fulfilled", (state, action) => {
      state.project.status = action.payload.status;
    });
    builder.addCase("editor/unsubmitProject/fulfilled", (state, action) => {
      state.project.status = action.payload.status;
    });
    builder.addCase("editor/submitProject/fulfilled", (state, action) => {
      state.project.status = action.payload.status;
    });
    builder.addCase("editor/returnProject/fulfilled", (state, action) => {
      state.project.status = action.payload.status;
    });
    builder.addCase("editor/completeProject/fulfilled", (state, action) => {
      state.project.status = action.payload.status;
    });
  },
});

// Action creators are generated for each case reducer function
export const {
  setIsRemix,
  updateUserType,
  addProjectComponent,
  loadingRunner,
  codeRunHandled,
  expireJustLoaded,
  closeFile,
  openFile,
  setOpenFiles,
  addFilePanel,
  setFocussedFileIndex,
  setPage,
  setEmbedded,
  setBrowserPreview,
  setError,
  setIsSplitView,
  setNameError,
  setWebComponent,
  setProject,
  setProjectIdentifier,
  setSenseHatAlwaysEnabled,
  setSenseHatEnabled,
  stopCodeRun,
  stopDraw,
  triggerCodeRun,
  triggerDraw,
  triggerSave,
  updateComponentName,
  updateImages,
  updateProjectComponent,
  updateProjectName,
  closeAccessDeniedNoAuthModal,
  closeAccessDeniedWithAuthModal,
  closeNotFoundModal,
  resetProjectLoadState,
  showProjectModal,
  closeProjectModal,
  showRenameProjectModal,
  closeRenameProjectModal,
  showDeleteProjectModal,
  closeDeleteProjectModal,
  setProjectIndexPage,
  showSidebar,
  hideSidebar,
  disableTheming,
} = EditorSlice.actions;

export default EditorSlice.reducer;
